/**
 * QuestionRenderer Component
 * Routes to the appropriate question component based on question type
 */

import { Question, FormAnswers } from '../../types/formConfig'
import RadioQuestion from './QuestionTypes/RadioQuestion'
import CheckboxQuestion from './QuestionTypes/CheckboxQuestion'
import TextQuestion from './QuestionTypes/TextQuestion'
import TextareaQuestion from './QuestionTypes/TextareaQuestion'
import SelectQuestion from './QuestionTypes/SelectQuestion'
import RangeQuestion from './QuestionTypes/RangeQuestion'
import AddressQuestion from './QuestionTypes/AddressQuestion'
import { loadPostcode } from '../../utils/postcodeStorage'

interface QuestionRendererProps {
  question: Question
  value: string | string[] | number | object
  onChange: (value: string | string[] | number | object) => void
  error?: string
  allAnswers?: FormAnswers // Needed for address questions to get postcode
}

const QuestionRenderer = ({
  question,
  value,
  onChange,
  error,
  allAnswers = {}
}: QuestionRendererProps) => {
  const renderQuestion = () => {
    switch (question.questionType) {
      case 'radio':
        return (
          <RadioQuestion
            questionId={question.id}
            options={question.options || []}
            value={String(value || '')}
            onChange={(val) => onChange(val)}
            required={question.required}
          />
        )

      case 'checkbox':
        return (
          <CheckboxQuestion
            questionId={question.id}
            options={question.options || []}
            value={Array.isArray(value) ? value.map(String) : []}
            onChange={(val) => onChange(val)}
            required={question.required}
          />
        )

      case 'textarea':
        return (
          <TextareaQuestion
            questionId={question.id}
            value={String(value || '')}
            onChange={(val) => onChange(val)}
            placeholder={question.placeholder}
            required={question.required}
            error={error}
          />
        )

      case 'text':
      case 'email':
      case 'tel':
      case 'postcode':
        return (
          <TextQuestion
            questionId={question.id}
            questionType={question.questionType}
            value={String(value || '')}
            onChange={(val) => onChange(val)}
            placeholder={question.placeholder}
            required={question.required}
            error={error}
          />
        )

      case 'select':
        return (
          <SelectQuestion
            questionId={question.id}
            options={question.options || []}
            value={String(value || '')}
            onChange={(val) => onChange(val)}
            placeholder={question.placeholder}
            required={question.required}
            error={error}
          />
        )

      case 'range':
        return (
          <RangeQuestion
            questionId={question.id}
            value={Number(value || question.min || 0)}
            onChange={(val) => onChange(val)}
            min={question.min}
            max={question.max}
            step={question.step}
            required={question.required}
            error={error}
          />
        )

      case 'address-select':
      case 'address-manual': {
        // Get postcode from localStorage or from a previous answer
        let postcodeValue = ''

        if (question.postcodeSource === 'localStorage') {
          postcodeValue = loadPostcode() || ''
        } else if (question.postcodeSource) {
          // Get from another question's answer
          postcodeValue = String(allAnswers[question.postcodeSource] || '')
        }

        if (!postcodeValue) {
          return (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800">
                Postcode not found. Please go back and enter your postcode.
              </p>
            </div>
          )
        }

        return (
          <AddressQuestion
            questionId={question.id}
            postcode={postcodeValue}
            value={value}
            onChange={(val) => onChange(val)}
            required={question.required}
            error={error}
          />
        )
      }

      default:
        return (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800">
              Question type "{question.questionType}" is not yet supported.
            </p>
          </div>
        )
    }
  }

  return (
    <div className="w-full">
      {/* Question Text */}
      <div className="mb-8">
        <h2 className="text-3xl md:text-4xl font-bold text-dark-plum mb-3">
          {question.questionText}
          {question.required && <span className="text-red-500 ml-1">*</span>}
        </h2>

        {question.helpText && (
          <p className="text-lg text-gray-600 leading-relaxed">
            {question.helpText}
          </p>
        )}
      </div>

      {/* Question Input */}
      <div className="mb-8">
        {renderQuestion()}
      </div>
    </div>
  )
}

export default QuestionRenderer
